/**
 * src/lib/api/analytics.js — Financial analytics API functions
 * Fetches data for CEO analytics dashboard (invoices, procurement, all entities).
 * Uses direct Supabase queries for procurement and inventory data.
 */

import { supabase } from '../supabase';

// Invoice data now comes from Supabase CFO tables
export { fetchCFOInvoices as fetchInvoices, fetchSupplierInvoicesCFO as fetchSupplierInvoices } from './cfo';

export async function fetchAllPurchaseOrders() {
  try {
    const { data, error } = await supabase
      .from('purchase_orders')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { console.warn('[analytics] PO fetch error:', error.message); return []; }
    return (data || []).map(row => ({ id: row.id, fields: row }));
  } catch { return []; }
}

export async function fetchAllPOLines() {
  // po_lines table does not exist in current schema — line items live inside
  // purchase_orders.items (jsonb) or are derived elsewhere. Short-circuit to
  // avoid a 404 on every analytics mount. The Overview no longer depends on this
  // (Slice B uses supplier_invoices totals instead).
  return [];
}

/**
 * Fetch agent analytics data (feature-scoped, not shared domain data).
 * Returns { sessions, toolLogs, gaps, requests }.
 */
export async function fetchAgentAnalytics(dayRange = 30) {
  const since = new Date(Date.now() - dayRange * 86400000).toISOString();
  try {
    const [sRes, tRes, gRes, rRes] = await Promise.all([
      supabase.from('agent_session_logs').select('topic, sentiment, confidence_score, persona, created_at')
        .gte('created_at', since).order('created_at', { ascending: false }),
      supabase.from('agent_tool_logs').select('tool_name, persona, created_at')
        .gte('created_at', since),
      supabase.from('knowledge_gaps').select('*').eq('resolved', false).order('frequency', { ascending: false }),
      supabase.from('feature_requests').select('*').order('upvotes', { ascending: false }).order('created_at', { ascending: false }).limit(20),
    ]);
    return {
      sessions: sRes.data || [],
      toolLogs: tRes.data || [],
      gaps: gRes.data || [],
      requests: rRes.data || [],
    };
  } catch {
    return { sessions: [], toolLogs: [], gaps: [], requests: [] };
  }
}

export async function fetchInventoryItems() {
  try {
    const { data, error } = await supabase
      .from('materials')
      .select('*')
      .order('material_name');
    if (error) { console.warn('[analytics] inventory fetch error:', error.message); return []; }
    return (data || []).map(row => ({ id: row.id, fields: row }));
  } catch { return []; }
}
